import { getWeek } from "date-fns";
import { COUNTRY_CODES, COUNTRY_PHONE_LIST, OTP_EXPIRY_TIMESTAMP } from "~/constants";
import { EMAIL_REGEX } from "~/constants/regex";
import { setItemToLocalStorage } from "./clientUtils";
import { AddressType } from "~/types-and-enums/generalTypes";

/**
 * Retrieves the title of the current document.
 *
 * @return {string} The title of the document, or an empty string if the document does not have a title.
 */
export const getDocumentTitle = () => {
  if (typeof window !== "undefined") {
    return document.getElementsByTagName("title")?.[0]?.innerHTML ?? "";
  }
  return "";
};

/**
 * Checks if the given email is associated with an employee.
 *
 * @param {string} email - The email to check.
 * @return {boolean} Returns true if the email is associated with an employee, false otherwise.
 */
export const checkUserIsEmployee = (email: string) => {
  const whiteList = ["aptstack@gmail.com", "andercoreexternal@gmail.com"];

  return email?.endsWith("@andercore.com") || whiteList.some((wlEmail) => wlEmail === email);
};

/**
 * Formats a number to a string with a fixed number of decimal places.
 * @param number
 * @returns
 */
export const formatNumber = (value: string | number, decimalSpaces: number = 2): string => {
  if (typeof value === "number") {
    return value % 1 === 0 ? value.toString() : value.toFixed(decimalSpaces);
  } else if (typeof value === "string") {
    const parsedValue: number = parseFloat(value);
    if (!isNaN(parsedValue)) {
      return parsedValue % 1 === 0 ? parsedValue.toString() : parsedValue.toFixed(decimalSpaces);
    }
  }
  return value; // Handles other data types
};

/**
 * Format an amount based on the specified currency and locale.
 * - localizeAmountWithCurrency
 * @param amount - The amount to be formatted.
 * @param currency - The currency code (e.g., 'EUR', 'USD').
 * @param locale - The locale code (e.g., 'de', 'en').
 * @returns The formatted currency string.
 */
export const formatCurrency = (amount: number = 0.0, currency: string = "EUR", locale: string = "de"): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount);
};

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Convert File to base64 string
 * @param file - The file to convert
 * @returns Promise that resolves to base64 string (without data URL prefix)
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const countNewlines = (text: string) => {
  let count = 0;
  let index = text.indexOf("\n");
  while (index !== -1) {
    count++;
    index = text.indexOf("\n", index + 1);
  }
  return count;
};

export const validateEmail = (email: string) => {
  return String(email).toLowerCase().match(EMAIL_REGEX);
};

// Function to deeply merge two objects
export const deepMerge = <T extends Record<string, unknown>, S extends Record<string, unknown>>(
  target: T,
  source: S
): T & S => {
  const result = target as T & S;
  for (const key in source) {
    const sourceValue = source[key];
    if (sourceValue instanceof Object && !Array.isArray(sourceValue)) {
      (result as Record<string, unknown>)[key] = deepMerge(
        ((target as Record<string, unknown>)[key] as Record<string, unknown>) || {},
        sourceValue as Record<string, unknown>
      );
    } else {
      (result as Record<string, unknown>)[key] = sourceValue;
    }
  }
  return result as unknown as T & S;
};

export enum ColorModeEnum {
  Stroke = "stroke",
  Fill = "fill",
}

/**
 * Calculate the difference in milliseconds between a future date and the current date
 * @param {string | Date} futureDate - The future date and time
 * @returns {number} - The difference in milliseconds
 */
export function getTimeDifferenceInMilliseconds(futureDate?: string | Date): number {
  if (!futureDate) return 0;

  const now: Date = new Date();
  const currentDate = now.getTime();
  const future = new Date(futureDate).getTime();

  return future > currentDate ? future - currentDate : 0;
}

/**
 * Converts milliseconds to days, hours, minutes and seconds
 * @param milliseconds
 * @returns
 */
export const getDaysHoureMinutesSeconds = (milliseconds: number, d: string, h: string, m: string, s: string) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const daysStr = `${days}${d} `;
  const hoursStr = `${hours}${h} `;
  const minutesStr = `${minutes}${m} `;
  const secondsStr = `${seconds}${s}`;

  if (days > 0) {
    return `${daysStr}${hoursStr}${minutesStr}${secondsStr}`;
  } else if (hours > 0) {
    return `${hoursStr}${minutesStr}${secondsStr}`;
  } else if (minutes > 0) {
    return `${minutesStr}${secondsStr}`;
  } else {
    return `${secondsStr}`;
  }
};

/**
 * Get the country code based on the country name
 * @param countryName - The name of the country
 * @returns The country code (e.g., 'DE')
 */
export const getCountryCode = (countryName?: string) => {
  if (!countryName) return ""; // If no country name is provided
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

  for (const code of COUNTRY_CODES) {
    const localizedCountryName = regionNames.of(code);
    if (
      localizedCountryName?.toLowerCase() === countryName?.toLowerCase() ||
      localizedCountryName?.includes(countryName)
    ) {
      return code.toUpperCase(); // Return in upper case, e.g., 'DE'
    }
  }

  return ""; // If no match is found
};

/**
 * Check if two objects are deeply equal
 * @param firstObject
 * @param secondObject
 * @returns
 */
export const isObjectsDeepEqual = (
  firstObject: Record<string, unknown>,
  secondObject: Record<string, unknown>
): boolean => {
  if (firstObject === secondObject) {
    return true;
  }

  if (
    typeof firstObject !== "object" ||
    firstObject == null ||
    typeof secondObject !== "object" ||
    secondObject == null
  ) {
    return false;
  }

  const keys1 = Object.keys(firstObject);
  const keys2 = Object.keys(secondObject);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every((key) =>
    isObjectsDeepEqual(firstObject[key] as Record<string, unknown>, secondObject[key] as Record<string, unknown>)
  );
};

export const findCountryByPhone = (phoneNumber: string) => {
  const countryMap: { [key: string]: string } = {};

  // Populate the map with country codes, use the country code as the key
  COUNTRY_PHONE_LIST.forEach((country) => {
    countryMap[country.code] = country.flagUrl;
  });

  // Sort the country list by the length of the country code, longest first
  const sortedCountryList = COUNTRY_PHONE_LIST.toSorted((a, b) => b.code.length - a.code.length);

  // Iterate through the sorted list to find the longest matching country code
  for (const country of sortedCountryList) {
    if (phoneNumber.startsWith(country.code)) {
      return country; // Return the entire country object
    }
  }
  // If no country is found, return germany as default
  return COUNTRY_PHONE_LIST[0];
};

/**
 * Check if a value is empty (null, undefined, empty array, or empty object)
 * @param value - {}, [], null, undefined
 * @returns true if the value is empty, false otherwise
 */
export const isEmpty = (value: unknown): boolean => {
  if (value == null || value === "") return true; // Check for null or undefined or empty string

  if (Array.isArray(value)) {
    return value.length === 0; // Check if array is empty
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0; // Check if object has no keys
  }

  return false;
};

export const isNotEmpty = (value: unknown): boolean => !isEmpty(value);

/**
 * Check if a value is a number
 * @param value - The value to check
 * @returns true if the value is a number, false otherwise
 */
export const isNumber = (value: string | number) => !isNaN(Number(value));

/**
 * Returns address details as string
 * - Combines street, postal_code city, and country
 * - if postal_code is not provided, it is omitted
 * - if city is not provided, it is omitted
 * - if country is not provided, it is omitted
 * @param street - Ex: 'Musterstraße 123'
 * @param postal_code - Ex: '12345'
 * @param city - Ex: 'Berlin'
 * @param country - Ex: 'Germany'
 * @returns - Ex: 'Musterstraße 123, 12345 Berlin, Germany'
 */
export const formatAddress = ({
  street,
  postal_code,
  city,
  country,
}: {
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
}): string => {
  const addressParts = [street, postal_code ? `${postal_code}${city ? " " + city : ""}` : city, country];

  return addressParts.filter(Boolean).join(", ");
};

export const formatAddressReturnOnlyPostalCodeAndCity = ({
  postal_code = "",
  city = "",
}: {
  postal_code?: string;
  city?: string;
}): string => {
  return postal_code ? `${postal_code}${city ? " " + city : ""}` : city ? city : "";
};

/**
 * Get the week number of the year
 * @param date
 * @returns Week number - Ex: 46
 */
export const getWeekNumber = (date: Date) => {
  return getWeek(date, {
    weekStartsOn: 1,
    firstWeekContainsDate: 4,
  });
};

/**
 * Check if the address is valid
 * @param address - The address to validate
 * @returns true if the address is valid, false otherwise
 */
export const isAddressValid = (address?: AddressType | null) => {
  const { street, postal_code, city, country } = address || {};
  return Boolean(street && postal_code && city && country);
};

/**
 * Check if the address is invalid
 * @param address - The address to validate
 * - street, postal_code, city, and country are required
 * @returns true if the address is invalid, false otherwise
 */
export const isAddressInvalid = (address?: AddressType | null) => {
  return !isAddressValid(address);
};

/**
 * Set the OTP expiry timestamp in local storage for 5 minutes
 * @returns The future date
 */
export const setOtpExpiryTimestamp = () => {
  const currentTime = new Date(); // current time in milliseconds
  // Add 5 minutes to the current time
  const futureTime = new Date(currentTime.getTime() + 5 * 60 * 1000); // 5 minutes in milliseconds
  setItemToLocalStorage(OTP_EXPIRY_TIMESTAMP, futureTime.toString());

  return futureTime;
};

/**
 * Add commas to a number
 * @param number
 * @returns
 */
export const addCommaToNumber = (number: number) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

/**
 * Get MIME type from file extension
 * @param extension - The file extension
 * @returns The MIME type
 */
export const getMimeTypesMap = (extension: string): string => {
  const mimeTypes: Record<string, string> = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    zip: "application/zip",
    txt: "text/plain",
  };
  return mimeTypes[extension.toLowerCase()] || "application/octet-stream";
};

/**
 * Handle API errors and return a user-friendly error message
 * @param error - The API error object
 * @param context - Optional context string for logging (e.g., "Failed to create job")
 * @returns Object with error message and errors object
 */
export const handleApiError = (
  error: unknown,
  context?: string
): {
  errorMessage: string;
  errors?: Record<string, string[]>;
} => {
  const apiError = error as {
    message: string;
    status: number;
    statusText: string;
    code?: string;
    errors?: Record<string, string[]>;
  };

  // Log error with context if provided
  if (context) {
    console.error(context, {
      message: apiError.message,
      status: apiError.status,
      statusText: apiError.statusText,
      code: apiError.code,
      errors: apiError.errors,
    });
  }

  // Provide more specific error message based on status code
  let errorMessage = apiError.message || "An error occurred";
  if (apiError.status === 403) {
    errorMessage = "Access forbidden. Please check your permissions or try logging in again.";
  } else if (apiError.status === 401) {
    errorMessage = "Authentication failed. Please try logging in again.";
  } else if (apiError.status === 400 || apiError.status === 422) {
    errorMessage = apiError.message || "Validation failed. Please check your input.";
  } else if (apiError.status === 404) {
    errorMessage = apiError.message || "Resource not found.";
  }

  return {
    errorMessage,
    errors: apiError.errors,
  };
};

/**
 * Check if a value is truly empty
 * Handles null, undefined, empty strings, empty objects, and objects with empty name property
 * @param value - The value to check
 * @returns true if the value is empty, false otherwise
 */
export const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "object" && Object.keys(value).length === 0) return true;
  if (typeof value === "object" && value !== null && "name" in value) {
    const nameValue = (value as { name?: unknown }).name;
    if (!nameValue || (typeof nameValue === "string" && nameValue.trim() === "")) {
      return true;
    }
  }
  return false;
};
